import { gql, type TypedDocumentNode } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import type { GQL } from "@binale-tech/shared";
import { use, useCallback } from "react";

import { CompanyContext } from "@domain/context/CompanyContext";

const dmsDocumentsAutoSort: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsDocumentsAutoSort">,
    GQL.IMutationDmsDocumentsAutoSortArgs
> = gql`
    mutation dmsDocumentsAutoSort($companyId: ID!, $documentIds: [ID!]) {
        dmsDocumentsAutoSort(companyId: $companyId, documentIds: $documentIds) {
            documentId
            outcome
            reason
            targetType
            targetSubType
            transactionId
            candidateTransactionIds
            isBewirtungCandidate
        }
    }
`;

export class DmsAutoSortNoResultError extends Error {}

export const useDmsAutoSort = (): ((documentIds: string[]) => Promise<GQL.IDmsAutoSortResult[]>) => {
    const { companyGQL } = use(CompanyContext);
    const [autoSort] = useMutation(dmsDocumentsAutoSort);
    const companyId = companyGQL?.id;

    return useCallback(
        async (documentIds: string[]) => {
            if (!companyId) {
                throw new Error("No active company");
            }
            const response = await autoSort({ variables: { companyId, documentIds } });
            const results = response.data?.dmsDocumentsAutoSort;
            if (!results) {
                throw new DmsAutoSortNoResultError("Auto-sort returned no data");
            }
            return results;
        },
        [autoSort, companyId],
    );
};
